import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        await register(email, password, name);
        toast.success('Compte créé avec succès');
      } else {
        await login(email, password);
        toast.success('Connexion réussie');
      }
      navigate('/admin');
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-cover bg-center relative"
      style={{
        backgroundImage: 'url(https://images.pexels.com/photos/37078712/pexels-photo-37078712.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
      }}
      data-testid="admin-login-page"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-stone-900/80" />
      
      {/* Back to site link */}
      <a 
        href="/"
        className="absolute top-6 left-6 z-10 flex items-center gap-2 text-stone-300 hover:text-white transition-colors duration-200 font-inter text-sm"
        data-testid="admin-back-to-site"
      >
        <ArrowLeft className="h-4 w-4" />
        Retour au site
      </a>
      
      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-white p-8 lg:p-10 shadow-2xl">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="font-playfair text-3xl font-bold text-stone-900">
              Versailles<span className="text-red-800">Plus</span>
            </h1>
            <p className="font-inter text-sm text-stone-500 mt-2">
              Espace Rédaction
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="font-inter text-stone-700">
                  Nom complet
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jean Dupont"
                  required={isRegister}
                  className="mt-2"
                  data-testid="admin-name-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="font-inter text-stone-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@versaillesplus.fr"
                required
                className="mt-2"
                data-testid="admin-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="font-inter text-stone-700">
                Mot de passe
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-2"
                data-testid="admin-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-red-800 hover:bg-red-900 text-white font-inter"
              data-testid="admin-submit-button"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                isRegister ? 'Créer un compte' : 'Se connecter'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              className="font-inter text-sm text-stone-500 hover:text-red-800 transition-colors duration-200"
              data-testid="admin-toggle-auth"
            >
              {isRegister 
                ? 'Déjà un compte ? Se connecter' 
                : 'Pas encore de compte ? S\'inscrire'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
